import React, { useContext, useEffect } from 'react'
import { IoClose } from "react-icons/io5";
import { FaHeart, FaShoppingBag, FaTrash, FaPlus, FaMinus, FaFileInvoice } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Context from '../context';
import displayINRCurrency from '../helpers/displayCurrency';
import wishlistHelper from '../helpers/wishlistHelper';
import addToCart from '../helpers/addToCart';
import SummaryApi from '../common';
import useTranslate from '../hooks/useTranslate';

const WishlistDrawer = ({ onClose, onOpenCart }) => {
    const { wishlistItems, fetchUserWishlist, fetchUserAddToCart, cartItems, setShowCart, languageCode, translateText } = useContext(Context)
    const user = useSelector(state => state?.user?.user)
    const navigate = useNavigate()
    const [translatedWishlist, setTranslatedWishlist] = React.useState([])

    const t = useTranslate({
        myWishlist: "My Wishlist",
        shopFavorite: "Shop Your Favorite Harvest",
        harvest: "Harvest",
        inCart: "In Cart",
        add: "Add",
        emptyWishlist: "Your wishlist is empty",
        assuredSelection: "FasalShop Assured Selection",
        secureTracking: "Secure Harvest Tracking & Digital Support",
        dismiss: "Dismiss",
        viewCart: "View My Cart"
    });

    const displayItems = wishlistItems?.length > 0 ? wishlistItems : [];

    useEffect(() => {
        const translateWishlist = async () => {
            if (!displayItems.length || !languageCode || languageCode === 'en') {
                setTranslatedWishlist(displayItems);
                return;
            }

            try {
                const translated = await Promise.all(displayItems.map(async (item) => {
                    const pName = item.itemname || item.productName || "Fasal Item";
                    const bName = item.subcategory_name || item.brandName || t.harvest;

                    const [tName, tBrand] = await Promise.all([
                        translateText(pName, languageCode),
                        translateText(bName, languageCode)
                    ]);

                    return {
                        ...item,
                        translatedName: tName,
                        translatedBrand: tBrand
                    };
                }));
                setTranslatedWishlist(translated);
            } catch (error) {
                console.error("Wishlist translation error:", error);
                setTranslatedWishlist(displayItems);
            }
        };
        translateWishlist();
    }, [displayItems, languageCode, translateText, t.harvest]);

    useEffect(() => {
        if (user?._id) {
            fetchUserWishlist()
        }
    }, [fetchUserWishlist, user?._id])

    useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const handleRemove = (e, id) => {
        wishlistHelper.addToWishlist(e, { _id: id })
        fetchUserWishlist()
    }

    const handleAddToCart = async (e, id) => {
        const userId = user?._id || user?.id;
        if (!userId) {
            navigate("/login");
            return;
        }
        await addToCart(e, id, userId)
        fetchUserAddToCart()
    }

    const updateQty = async (e, id, qty, isDummy, dummyId, change) => {
        e?.stopPropagation()
        e?.preventDefault()

        if (isDummy) {
            let cart = JSON.parse(localStorage.getItem('dummyCart')) || []
            const item = cart.find(i => i.productId === dummyId)
            if (item) {
                if (change === -1 && item.quantity === 1) {
                    cart = cart.filter(i => i.productId !== dummyId)
                } else {
                    item.quantity += change
                }
                localStorage.setItem('dummyCart', JSON.stringify(cart))
                fetchUserAddToCart()
            }
            return
        }

        const newQty = qty + change
        if (newQty === 0) {
            const response = await fetch(SummaryApi.deleteCartProduct.url, {
                method: SummaryApi.deleteCartProduct.method,
                credentials: 'include',
                headers: { "content-type": 'application/json' },
                body: JSON.stringify({ _id: id })
            })
            const responseData = await response.json()
            if (responseData.success) { fetchUserAddToCart() }
        } else {
            const response = await fetch(SummaryApi.updateCartProduct.url, {
                method: SummaryApi.updateCartProduct.method,
                credentials: 'include',
                headers: { "content-type": 'application/json' },
                body: JSON.stringify({ _id: id, quantity: newQty })
            })
            const responseData = await response.json()
            if (responseData.success) { fetchUserAddToCart() }
        }
    }

    return (
        <div className='fixed inset-0 z-[110] flex justify-end overflow-hidden'>
            <div className='absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in transition-opacity duration-300' onClick={onClose}></div>

            <div className='relative w-full max-w-[420px] bg-[#fdfdfd] h-full shadow-2xl flex flex-col transform transition-transform duration-500 animate-in slide-in-from-right-full'>

                {/* BRANDED HEADER */}
                <div className='shrink-0 bg-[#143d2c] pt-12 pb-10 px-6 relative overflow-hidden flex flex-col items-center justify-center text-center'>
                    <button
                        onClick={onClose}
                        className='absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 rounded-full text-white/80 hover:text-white transition-all z-20'
                    >
                        <IoClose size={20} />
                    </button>

                    <div className='relative z-10 flex flex-col items-center'>
                        <div className='w-20 h-20 rounded-full border-2 border-[#f39200] flex items-center justify-center mb-6 shadow-2xl'>
                            <div className='w-full h-full rounded-full bg-black/10 flex items-center justify-center'>
                                <FaHeart size={30} className='text-[#f39200]' />
                            </div>
                        </div>

                        <h2 className='text-3xl font-black text-white uppercase tracking-wider mb-2'>{t.myWishlist}</h2>
                        <p className='text-[10px] text-[#f39200] font-black uppercase tracking-[2px]'>{t.shopFavorite}</p>
                    </div>
                </div>

                {/* Content Area */}
                <div className='flex-1 overflow-y-auto custom-scrollbar bg-[#fcfdfc]'>
                    <div className='p-4 space-y-4'>
                        {translatedWishlist.length > 0 ? (
                            translatedWishlist.map((product) => {
                                const cartItem = cartItems?.find(item =>
                                    (item.productId?._id === product?._id) || (item.productId === product?._id)
                                )
                                const isInCart = !!cartItem
                                const productRate = product?.item_rates?.[0] || product?.rates?.[0] || {}
                                const productSellingPrice = productRate.salerate || product?.sellingPrice || 0
                                const productPrice = productRate.mrp || product?.price || 0

                                return (
                                    <div key={product._id} className='bg-white border border-slate-50 rounded-[2rem] p-4 flex gap-4 hover:shadow-md transition-all group relative overflow-hidden'>
                                        {/* Remove Icon */}
                                        <button
                                            onClick={(e) => handleRemove(e, product._id)}
                                            className='absolute top-4 right-4 text-slate-200 hover:text-red-500 transition-all z-10'
                                        >
                                            <FaTrash size={12} />
                                        </button>

                                        {/* Image */}
                                        <div className='w-24 h-24 bg-slate-50 rounded-2xl p-2 shrink-0 flex items-center justify-center border border-slate-50'>
                                            <Link to={`/product/${product._id || product.id}`} onClick={onClose} className='w-full h-full'>
                                                <img
                                                    src={(product?.image && product?.image[0]) || product?.productImage?.[0] || ""}
                                                    alt={product?.itemname || product?.productName}
                                                    className='w-full h-full object-contain mix-blend-multiply'
                                                />
                                            </Link>
                                        </div>

                                        {/* Details */}
                                        <div className='flex-1 flex flex-col justify-between py-1'>
                                            <div>
                                                <span className='text-[8px] font-black text-[#00b259] uppercase tracking-[2px] block mb-1'>{product?.translatedBrand || product?.subcategory_name || product?.brandName || t.harvest}</span>
                                                <Link
                                                    to={`/product/${product._id || product.id}`}
                                                    onClick={onClose}
                                                    className='text-[13px] font-black text-[#143d2c] leading-tight line-clamp-2 hover:text-[#00b259] transition-colors uppercase tracking-tight'
                                                >
                                                    {product?.translatedName || product?.itemname || product?.productName}
                                                </Link>
                                            </div>

                                            <div className='flex items-center justify-between mt-2'>
                                                <div className='flex flex-col'>
                                                    <span className='text-base font-black text-[#143d2c]'>
                                                        {displayINRCurrency(productSellingPrice)}
                                                    </span>
                                                    {productPrice > productSellingPrice && (
                                                        <span className='text-[10px] text-slate-300 font-bold line-through'>
                                                            {displayINRCurrency(productPrice)}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className='flex gap-2'>
                                                    {isInCart ? (
                                                        <button
                                                            onClick={(e) => { e?.preventDefault(); onClose(); setShowCart(true); }}
                                                            className='bg-[#143d2c] text-white py-2 px-3 rounded-xl border border-[#00b259]/20 transition-all text-[9px] font-black uppercase whitespace-nowrap shadow-sm'
                                                        >
                                                            {t.inCart}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => handleAddToCart(e, product._id)}
                                                            className='bg-white text-[#143d2c] border-2 border-[#143d2c] py-2 px-4 rounded-xl shadow-sm hover:bg-[#143d2c] hover:text-white transition-all text-[9px] font-black uppercase'
                                                        >
                                                            {t.add}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        ) : (
                            <div className='h-full flex flex-col items-center justify-center py-20 opacity-30'>
                                <FaHeart size={50} className='text-slate-200 mb-4' />
                                <h3 className='text-xs font-black text-[#143d2c] uppercase tracking-wider'>{t.emptyWishlist}</h3>
                            </div>
                        )}
                    </div>
                </div>

                {/* PREMIUM FOOTER */}
                <div className="p-5 bg-white border-t border-slate-50 mt-auto shadow-[0_-10px_30px_rgba(0,0,0,0.03)] flex flex-col gap-4">
                    <div className='flex items-center gap-3 px-1'>
                        <div className='w-9 h-9 bg-slate-50 text-[#143d2c] rounded-xl flex items-center justify-center border border-slate-100'>
                            <FaFileInvoice size={15} />
                        </div>
                        <div className='flex flex-col'>
                            <p className='text-[8px] font-black text-[#143d2c] uppercase leading-none'>{t.assuredSelection}</p>
                            <p className='text-[7px] text-slate-400 font-bold mt-0.5'>{t.secureTracking}</p>
                        </div>
                    </div>

                    <div className='flex gap-4'>
                        <button
                            onClick={onClose}
                            className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-400 font-black py-4 rounded-2xl flex items-center justify-center gap-2 transition-all uppercase tracking-[2px] text-[10px] border border-slate-100 active:scale-95"
                        >
                            {t.dismiss}
                        </button>
                        <button
                            onClick={() => { if (onOpenCart) { onOpenCart(); } else { onClose(); setShowCart(true); } }}
                            className='flex-[2] bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] text-white font-black py-4 rounded-2xl shadow-xl hover:brightness-110 transition-all text-[10px] flex items-center justify-center gap-3 uppercase tracking-[3px]'
                        >
                            <FaShoppingBag /> {t.viewCart}
                        </button>
                    </div>
                </div>
            </div>


            <style dangerouslySetInnerHTML={{
                __html: `
 to { transform: translateX(0); }
 }
 @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
 @keyframes float {
 0% { transform: translateY(0px); }
 50% { transform: translateY(-10px); }
 100% { transform: translateY(0px); }
 }
 .animate-slideInRight { animation: slideInRight 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
 .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
 .animate-float { animation: float 3s ease-in-out infinite; }
 .custom-scrollbar::-webkit-scrollbar { width: 4px; }
 .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
 ` }} />
        </div>
    )
}

export default WishlistDrawer
